import React from 'react';
import { PermissionObject } from '@/types/pages/admin/permission';
import { useAppContext } from '@/provider/contexts';
import ForbiddenPage from '@/pages/ForbiddenPage';
import { BaseAbility } from '@/types/pages/admin/role';
import { LoginResponse } from '../types';

type PermissionItem = string | BaseAbility;

export function rawStringArrayToObject(inputArray: PermissionItem[]) {
    const output: PermissionObject = {
        page: {},
        system: [],
    };
    const map = new Map();
    inputArray.forEach((item) => {
        const value = typeof item === 'object' ? item.ability_name : item;

        if (!value) return;
        const [prefix, subkey1, subkey2, action] = value.split('-');
        const key = value.substring(0, value.lastIndexOf('-'));

        if (prefix === 'page') {
            if (action === 'read') {
                map.set(key, (item as BaseAbility).order_idx ?? null);
            }

            if (!map.get(key)) return;
            output.page[key] = {
                name:
                    subkey1.charAt(0).toUpperCase() +
                    subkey1.slice(1) +
                    ' ' +
                    subkey2.charAt(0).toUpperCase() +
                    subkey2.slice(1),
                actions: output.page[key]?.actions || [],
            };

            const el: { id?: number | undefined; action: string; value: string; order_idx?: number } = {
                action: action.toLowerCase(),
                value,
                order_idx: typeof item === 'object' ? item.order_idx : undefined,
            };

            if (typeof item === 'object') {
                el.id = item.id;
            }

            output.page[key].actions.push(el);
        } else if (prefix === 'system') {
            const el: { id?: number | undefined; name: string; value: string } = {
                name:
                    typeof item === 'object'
                        ? item.ability_description || ''
                        : value
                              .replace(/-/g, ' ')
                              .split(' ')
                              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' '),
                value: value,
            };

            if (typeof item === 'object') {
                el.id = item.id;
            }
            output[prefix].push(el);
        }
    });

    return output;
}

export function objectToRawStringArray(inputObject: PermissionObject) {
    const result: string[] = [];

    for (const pageKey in inputObject.page) {
        const page = inputObject.page[pageKey];
        page.actions.forEach((action) => {
            result.push(action.value);
        });
    }

    if (inputObject.system) {
        inputObject.system.forEach((action) => {
            result.push(action.value);
        });
    }

    return result;
}

interface WithPermissionProps {
    permission: string | string[];
    alt?: React.ComponentType | React.ReactElement;
    children?: React.ReactNode;
    [key: string]: unknown;
}

export function withPermission(WrappedComponent: React.ComponentType | React.ElementType) {
    return (props: WithPermissionProps) => {
        const { permission, alt, ...restProps } = props;
        const checkPermission = useCheckPermission();
        const hasPermission = checkPermission(permission);

        if (!hasPermission) {
            return alt ? React.isValidElement(alt) ? alt : React.createElement(alt as React.ComponentType) : <></>;
        }

        return <WrappedComponent {...restProps}></WrappedComponent>;
    };
}

export function withPermissionHOC(permission: string, alt?: React.ComponentType | React.ReactElement) {
    return function (WrappedComponent: React.ComponentType | React.ElementType) {
        return function () {
            const { userPermission, userDetails } = useAppContext();

            const checkPermission = useCheckPermission();
            const hasPermission = checkPermission(permission);

            if (!hasPermission) {
                return alt ? (
                    React.isValidElement(alt) ? (
                        alt
                    ) : (
                        React.createElement(alt as React.ComponentType)
                    )
                ) : (
                    <ForbiddenPage />
                );
            }

            if (!userDetails || !userPermission) {
                return <></>;
            }
            return <WrappedComponent />;
        };
    };
}

function _checkPermission(permissions: string | string[], userPermission: string[], userDetails: LoginResponse | null) {
    if (!userDetails || !userPermission) {
        console.warn('User is logging out, skipping permission check.');
        return false;
    }
    const required = Array.isArray(permissions) ? permissions : [permissions];
    return ['*', ...required].some((p) => userPermission.includes(p));
}

export function useCheckPermission() {
    const { userPermission, userDetails } = useAppContext();
    return (permission: string | string[]) => _checkPermission(permission, userPermission, userDetails);
}
